import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  styled,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Field, Formik, Form } from "formik";
import * as yup from "yup";
import { TextField } from "formik-material-ui";
import axios from "axios";
import { REIMBURSEMENT_API_ENDPOINTS } from "../../apiHelpers/apiConstants";
import { ERROR_MESSAGES } from "../../apiHelpers/errorConstants";
import {
  FileUploader,
  isSafeFilename,
  checkHarmfulPdfContent,
} from "../../utils/fileUploader";
import { useCallback } from "react";
import uploadIcon from "../../utils/images/upload-04.svg";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import BankDocumentPreview from "./BankDocumentPreview";
import BankDocumentPreviewModal from "./BankDocumentPreviewModal";
import DeleteIcon from "../../utils/images/delete-02.svg";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            width: "24px",
            height: "24px",
            color: "#252D38",
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const customTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FFF",
    "& fieldset": {
      borderColor: "#DBE0E6",
    },
    "&:hover fieldset": {
      borderColor: "#DBE0E6",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#DBE0E6",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    color: "#252D38",
    padding: "14px",
    fontSize: "16px",
    height: "24px",
    fontWeight: 400,
  },
  "& .MuiInputLabel-root": {
    color: "#47566A",
    fontWeight: 300,
  },
};

const AutoPayBankPopup = ({
  open,
  handleClose,
  users,
  accessToken,
  loadBankDetails,
  onAddSuccess,
  irflag = false,
  existingBank = null,
}) => {
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [bankFile, setBankFile] = useState([]);
  const [fileError, setFileError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [mbPayoutId, setMbPayoutId] = useState("");
  const fileRef = useRef();
  const [previewModal, setPreviewModal] = useState({
    open: false,
    url: null,
    isPdf: false,
    name: "",
  });

  const handleDocumentUpload = async (files, setFieldValue) => {
    setFileError("");
    let uploadedFiles = Array.from(files);

    if (uploadedFiles.length === 0) return;
    // Checks
    let zerobyteflag = 0;
    let documentType = false;
    let fileSizeOriginal = 0;

    for (let i = 0; i < uploadedFiles.length; ++i) {
      if (uploadedFiles[i]?.size === 0) {
        zerobyteflag = 1;
        break;
      }
      fileSizeOriginal += uploadedFiles[i]?.size / 1000;

      if (!isSafeFilename(uploadedFiles[i]?.name)) {
        setFileError(ERROR_MESSAGES.UNSUPPORTED_FILENAME);
        return;
      }
      if (
        !(
          (uploadedFiles[i]?.type === "application/pdf") |
          (uploadedFiles[i]?.type === "image/png") |
          (uploadedFiles[i]?.type === "image/jpg") |
          (uploadedFiles[i]?.type === "image/jpeg")
        )
      ) {
        documentType = true;
      }
    }

    try {
      const isPdfSafe = await checkHarmfulPdfContent(uploadedFiles);
      if (!isPdfSafe) {
        setFileError(ERROR_MESSAGES.INVALID_PDF_CONTENT);
        return;
      }
    } catch (error) {
      setFileError(ERROR_MESSAGES.PDF_VALIDATION_ERROR);
      return;
    }

    if (zerobyteflag) {
      setFileError(ERROR_MESSAGES.ZERO_BYTE_FILE);
      return;
    }
    if (documentType) {
      setFileError(ERROR_MESSAGES.INVALID_FILE_TYPE);
      return;
    }
    if (fileSizeOriginal > 10000) {
      setFileError(ERROR_MESSAGES.FILE_SIZE_EXCEEDED);
      return;
    }

    setBankFile(uploadedFiles);
    setFieldValue("file", uploadedFiles[0]);
  };

  const handlePreview = useCallback((fileObject, previewUrl, isPdf, fileName) => {
    let url = previewUrl;
    if (isPdf && fileObject instanceof Blob) {
      try {
        url = URL.createObjectURL(fileObject);
      } catch (error) {
        console.error("Error creating PDF URL:", error);
        return;
      }
    }
    if (url) {
      setPreviewModal({
        open: true,
        url: url,
        isPdf: isPdf,
        name: fileName,
      });
    }
  }, []);

  const handleDeleteDocument = useCallback(() => {
    setBankFile([]);
    setFileError("");
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModal((prev) => {
      if (prev.url && prev.isPdf) {
        URL.revokeObjectURL(prev.url);
      }
      return {
        open: false,
        url: null,
        isPdf: false,
        name: "",
      };
    });
  }, []);

  const verifyBankDetails = async (payload) => {
    let apiUrl = process.env.REACT_APP_REIMBURSEMENT_URL;
    try {
      const response = await axios.post(
        `${apiUrl}${REIMBURSEMENT_API_ENDPOINTS.VERIFY_BANK_ACCOUNT}`,
        payload,
        {
          headers: {
            authtoken: accessToken,
            "Content-Type": "application/json",
          },
        },
      );

      return response?.data;
    } catch (error) {
      console.error("Verification API Error", error);
      return { IsSuccess: false, Message: "API call failed" };
    }
  };

  const uploadManualDocument = async () => {
    if (bankFile.length === 0) {
      setFileError(ERROR_MESSAGES.NO_BANK_DOCUMENT);
      return null;
    }

    let fileName = bankFile[0]?.name;
    var today = new Date().toISOString().replace(/[:.]/g, "_");
    fileName =
      "PAT_" +
      users?.mbUserId +
      "_" +
      Math.random().toString(16).slice(2) +
      "_" +
      today +
      "." +
      fileName?.split(".")?.pop();

    let fileType = "BankDocuments";
    try {
      let imageType = bankFile[0]?.type;
      let singleFiledata = new File([bankFile[0]], fileName, {
        type: imageType,
      });

      const fileUploadResponse = await FileUploader.uploadFileData(
        users?.pmEntityId,
        singleFiledata,
        fileType,
      );
      if (fileUploadResponse?.isSuccess) {
        return fileUploadResponse?.fileName;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      open={open}
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: { xs: "95%", md: "900px" },
          maxWidth: "100%",
          borderRadius: "16px",
          minHeight: "500px",
        },
      }}
    >
      <BootstrapDialogTitle onClose={handleClose}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 500, fontSize: "18px", color: "#252D38" }}
        >
          Bank Account Details
        </Typography>
      </BootstrapDialogTitle>

      <DialogContent>
        {verificationFailed && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              marginBottom: 2,
              color: "#AE0006",
              fontSize: "14px",
              fontWeight: 400,
            }}
          >
            We're not able to fetch the bank details for the given account,
            could you please provide the following additional information.
          </Typography>
        )}

        <Box
          sx={{
            backgroundColor: "#FFFAF6",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #FEEBDA",
            marginBottom: "24px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              marginBottom: "8px",
              fontSize: "15px",
              color: "#252D38",
            }}
          >
            Please ensure the following with respect to the bank account
            details:
          </Typography>
          <ol
            style={{
              margin: 0,
              paddingLeft: "24px",
              listStyleType: "decimal",
              fontWeight: 400,
              fontSize: "14px",
              color: "#252D38",
            }}
          >
            <li>
              The bank details should only belong to the employee and not any of
              his/her dependents.
            </li>
            {verificationFailed && (
              <li>
                The bank account proof (passbook/statement/cheque book) should
                have Bank Account Holder Name, IFSC Code and Bank Account number
                mandatorily.
              </li>
            )}
          </ol>
        </Box>

        <Formik
          enableReinitialize={false}
          initialValues={{
            AccountNo: existingBank ? existingBank.accountholdernumber : "",
            ReAccountNo: existingBank ? existingBank.accountholdernumber : "",
            IFSCcode: existingBank ? existingBank.ifsccode : "",
            Bankname: existingBank ? existingBank.bankname : "",
            BranchName: existingBank ? existingBank.branchname : "",
            ACHolderName:
              existingBank?.accountholdername ||
              existingBank?.benef_name_bank_returned ||
              "",
            file: null,
          }}
          validationSchema={yup.object().shape({
            AccountNo: yup
              .string()
              .required("Account Number is Required")
              .matches(/^[0-9]+$/, "Account number must be in numbers")
              .min(5, "Must be min 5 digits")
              .max(18, "Cannot exceed 18 digits"),
            ReAccountNo: yup
              .string()
              .oneOf([yup.ref("AccountNo"), null], "Account numbers must match")
              .required("Re-enter Account Number is Required"),
            IFSCcode: yup
              .string()
              .required("IFSC Code Is Required")
              .transform((value) => value?.toUpperCase())
              .matches(
                /^[A-Z]{4}0[A-Z0-9]{6}$/,
                "IFSC code must be 4 uppercase letters, followed by 0, then 6 alphanumeric characters",
              ),
            // Conditional validation based on verificationFailed state
            Bankname: yup.string().when([], {
              is: () => verificationFailed,
              then: (schema) =>
                schema
                  .required("Bank name is Required")
                  .matches(/^[A-Za-z ]+$/, "Invalid format"),
              otherwise: (schema) => schema.notRequired(),
            }),
            BranchName: yup.string().when([], {
              is: () => verificationFailed,
              then: (schema) => schema.required("Branch name is Required"),
              otherwise: (schema) => schema.notRequired(),
            }),
            ACHolderName: yup.string().when([], {
              is: () => verificationFailed,
              then: (schema) =>
                schema.required("Account Holder name is Required"),
              otherwise: (schema) => schema.notRequired(),
            }),
          })}
          onSubmit={async (
            values,
            { setSubmitting, setFieldValue, setFieldError, setErrors },
          ) => {
            setSubmitting(true);

            if (!verificationFailed) {
              const payload = {
                bankAccountNumber: values.AccountNo,
                ifscCode: values.IFSCcode.toUpperCase(),
              };
              const result = await verifyBankDetails(payload);
              if (result && result.IsSuccess) {
                if (result.BankName) setFieldValue("Bankname", result.BankName);
                if (result.BranchName)
                  setFieldValue("BranchName", result.BranchName);
                if (result.AccountHolderName)
                  setFieldValue("ACHolderName", result.AccountHolderName);
                onAddSuccess("pennyDrop");
                handleClose();
              } else {
                if (result && result.mbPayoutId) {
                  setMbPayoutId(result.mbPayoutId);
                }
                setVerificationFailed(true);
              }
            } else {
              const documentKey = await uploadManualDocument();
              if (documentKey) {
                const payload = {
                  bankAccountNumber: values.AccountNo,
                  ifscCode: values.IFSCcode.toUpperCase(),
                  accountHolderName: values.ACHolderName,
                  bankName: values.Bankname,
                  branchName: values.BranchName,
                  document: documentKey,
                  mbPayoutId: mbPayoutId,
                };
                const result = await verifyBankDetails(payload);
                if (result && result.IsSuccess) {
                  onAddSuccess("manualDetails");
                  handleClose();
                } else {
                  setFileError(
                    result?.Message ||
                      ERROR_MESSAGES.BANK_ACCOUNT_ADD_FAILED,
                  );
                }
              } else {
                setFileError(ERROR_MESSAGES.DOCUMENT_UPLOAD_FAILED);
              }
            }

            setSubmitting(false);
          }}
        >
          {({ isSubmitting, setFieldValue, isValid, dirty }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={TextField}
                    name="AccountNo"
                    label="Enter Account No."
                    fullWidth
                    required
                    sx={customTextFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={TextField}
                    name="ReAccountNo"
                    label="Re-enter Account No."
                    fullWidth
                    required
                    sx={customTextFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={TextField}
                    name="IFSCcode"
                    label="IFSC Code"
                    fullWidth
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    required
                    sx={customTextFieldStyle}
                  />
                </Grid>

                {verificationFailed && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          marginTop: 1,
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#252D38",
                        }}
                      >
                        Additional details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        component={TextField}
                        name="Bankname"
                        label="Bank Name"
                        fullWidth
                        required
                        sx={customTextFieldStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        component={TextField}
                        name="BranchName"
                        label="Branch Name"
                        fullWidth
                        required
                        sx={customTextFieldStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        component={TextField}
                        name="ACHolderName"
                        label="Account Holder Name"
                        fullWidth
                        required
                        sx={customTextFieldStyle}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, marginTop: 2 }}
                      >
                        Upload Document
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          marginBottom: 1,
                        }}
                      >
                        <InfoOutlined color="primary" fontSize="small" />
                        <Typography variant="caption" color="textSecondary">
                          Cheque leaf/Bank passbook's front page
                        </Typography>
                      </Box>

                      <input
                        ref={fileRef}
                        hidden
                        id="file"
                        type="file"
                        accept="image/*, application/pdf"
                        onChange={(e) =>
                          handleDocumentUpload(e.target.files, setFieldValue)
                        }
                      />

                      <Box
                        sx={{
                          minHeight: {
                            xs: "200px",
                            sm: "220px",
                          },
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        {bankFile.length === 0 ? (
                          <Box
                            onClick={() => {
                              fileRef.current.click();
                            }}
                            sx={{
                              border: "1px solid #DBE0E6",
                              borderRadius: "9px",
                              padding: "12px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              backgroundColor: "#FFFFFF",
                              width: {
                                xs: "120px",
                                sm: "135px",
                                md: "130px",
                              },
                              height: {
                                xs: "120px",
                                sm: "135px",
                                md: "130px",
                              },
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: "#0066DC",
                                backgroundColor: "#F8FBFF",
                              },
                            }}
                          >
                            <img
                              src={uploadIcon}
                              alt="Upload"
                              style={{
                                width: "30px",
                                height: "30px",
                                marginBottom: "8px",
                                color: "#252D38",
                              }}
                            />
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#252D38",
                                textAlign: "center",
                              }}
                            >
                              Upload Document
                            </Typography>
                          </Box>
                        ) : (
                          <BankDocumentPreview
                            bankFile={bankFile}
                            onPreview={handlePreview}
                            onDelete={handleDeleteDocument}
                            DeleteIcon={DeleteIcon}
                          />
                        )}
                      </Box>

                      {fileError && (
                        <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
                          {fileError}
                        </Typography>
                      )}
                    </Grid>
                  </>
                )}

                {!verificationFailed && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ paddingY: 2 }}>
                        <Divider
                          sx={{ borderStyle: "dashed", borderColor: "#D0D5DD" }}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "15px",
                              lineHeight: 1.5,
                              color: "#4D5C6F",
                              fontWeight: 400,
                            }}
                          >
                            I confirm that the bank account information provided
                            is accurate and relates to my bank account. I
                            understand that any inaccuracy may result in denial
                            or cancellation of this claim at the Company's
                            discretion, for which I shall be solely responsible.
                            I acknowledge that the Company may verify the
                            information independently and may take appropriate
                            administrative or legal action if it is found to be
                            false or misleading.
                          </Typography>
                        }
                        sx={{ alignItems: "flex-start", margin: 0, gap: 1 }}
                      />
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 1,
                  }}
                >
                  {isSubmitting ? (
                    <Button
                      fullWidth
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #72B3FF",
                        backgroundColor: "#E2F0FF",
                        color: "#0066DC",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "16px",
                        height: "44px",
                        "&:disabled": {
                          backgroundColor: "#E2F0FF",
                          color: "#0066DC",
                          border: "1px solid #72B3FF",
                        },
                        "&:hover": {
                          backgroundColor: "#E2F0FF",
                        },
                      }}
                      disabled
                    >
                      <CircularProgress
                        size={20}
                        sx={{ color: "#0066DC", marginRight: "8px" }}
                      />
                      {verificationFailed
                        ? "Adding Account Details"
                        : "Fetching Account Details"}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                          width: "48%",
                          borderRadius: "8px",
                          textTransform: "none",
                          borderColor: "#A4B0C0",
                          color: "#47566A",
                          fontWeight: 600,
                          height: "44px",
                          fontSize: "15px",
                          "&:hover": {
                            borderColor: "#A4B0C0",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          isSubmitting ||
                          (!verificationFailed && !confirmed) ||
                          !isValid ||
                          !dirty ||
                          (verificationFailed && bankFile.length === 0)
                        }
                        sx={{
                          width: "48%",
                          borderRadius: "8px",
                          textTransform: "none",
                          backgroundColor: "#0066DC",
                          fontWeight: 600,
                          height: "44px",
                          "&:disabled": {
                            backgroundColor: "#DBE0E6",
                            border: "1px solid #BFC8D2",
                          },
                        }}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <BankDocumentPreviewModal
        open={previewModal.open}
        onClose={handleClosePreview}
        previewData={previewModal}
      />
    </BootstrapDialog>
  );
};

export default AutoPayBankPopup;
